import type { ContentTypeConditionSet, ServiceRecord } from "@/types/category";
import type { ContentTypeId } from "@/types/taxonomy";

/**
 * カテゴリ補完設定（デモデータ）。
 *
 * 商品形式・サービス区分・フロア・ジャンル・属性は別の軸なので、ここでも混ぜない。
 * 外部APIから取れない「日本語の見せ方」「どの条件を出すか」だけをユメサーチ側で補完する。
 * 実APIへ移行するときは、このファイルを API レスポンスに置き換える（UIは変更不要）。
 */

/** サービス区分（提供元のサービス単位。デモであることを名前で明示する） */
export const services: ServiceRecord[] = [
  {
    id: "svc-demo-all",
    name: "ユメサーチ総合（デモ）",
    providerServiceId: "DEMO-ALL",
    description: "デモ用の総合サービス区分です。すべての商品形式を扱います。",
    contentTypeIds: ["video", "manga", "ebook", "book", "doujin", "voice", "cg", "game", "goods", "other"],
    floorIds: ["fl-story", "fl-char", "fl-mood", "fl-media", "fl-collect"],
    source: "demo",
  },
  {
    id: "svc-demo-video",
    name: "映像・音声（デモ）",
    providerServiceId: "DEMO-VIDEO",
    description: "動画と音声作品を扱うデモ用のサービス区分です。",
    contentTypeIds: ["video", "voice"],
    floorIds: ["fl-story", "fl-char", "fl-mood", "fl-media"],
    source: "demo",
  },
  {
    id: "svc-demo-book",
    name: "書籍・同人（デモ）",
    providerServiceId: "DEMO-BOOK",
    description: "漫画・電子書籍・書籍・同人を扱うデモ用のサービス区分です。",
    contentTypeIds: ["manga", "ebook", "book", "doujin"],
    floorIds: ["fl-story", "fl-char", "fl-mood", "fl-media"],
    source: "demo",
  },
  {
    id: "svc-demo-game",
    name: "ゲーム・CG（デモ）",
    providerServiceId: "DEMO-GAME",
    description: "ゲームとCG集を扱うデモ用のサービス区分です。",
    contentTypeIds: ["game", "cg"],
    floorIds: ["fl-story", "fl-char", "fl-mood"],
    source: "demo",
  },
  {
    id: "svc-demo-goods",
    name: "グッズ・通販（デモ）",
    providerServiceId: "DEMO-GOODS",
    description: "グッズを扱うデモ用のサービス区分です。",
    contentTypeIds: ["goods"],
    floorIds: ["fl-collect"],
    source: "demo",
  },
];

/**
 * 商品形式ごとに出す検索条件。
 * personRoles は「作品の creator / performers のどちらを人物条件の対象にするか」。
 */
export const contentTypeConditionSets: Record<ContentTypeId, ContentTypeConditionSet> = {
  video: {
    contentType: "video",
    personLabel: "出演者",
    personRoles: ["performer", "creator"],
    makerLabel: "メーカー・レーベル",
    attributeIds: ["at-volume", "at-format"],
    providerAttributeIds: ["at-volume"],
  },
  manga: {
    contentType: "manga",
    personLabel: "作者",
    personRoles: ["creator"],
    makerLabel: "出版社・レーベル",
    attributeIds: ["at-pages", "at-format"],
    providerAttributeIds: ["at-pages"],
  },
  ebook: {
    contentType: "ebook",
    personLabel: "作者",
    personRoles: ["creator"],
    makerLabel: "出版社・レーベル",
    attributeIds: ["at-pages", "at-format"],
    providerAttributeIds: ["at-pages"],
  },
  book: {
    contentType: "book",
    personLabel: "作者・撮影",
    personRoles: ["creator"],
    makerLabel: "出版社",
    attributeIds: ["at-pages"],
    providerAttributeIds: ["at-pages"],
  },
  doujin: {
    contentType: "doujin",
    personLabel: "作者・サークル",
    personRoles: ["creator"],
    makerLabel: "サークル・出版社",
    attributeIds: ["at-pages", "at-format"],
    providerAttributeIds: ["at-pages"],
  },
  voice: {
    contentType: "voice",
    personLabel: "出演",
    personRoles: ["performer", "creator"],
    makerLabel: "レーベル",
    attributeIds: ["at-tracks", "at-format"],
    providerAttributeIds: ["at-tracks"],
  },
  cg: {
    contentType: "cg",
    personLabel: "作者",
    personRoles: ["creator"],
    makerLabel: "サークル・メーカー",
    attributeIds: ["at-platform"],
    providerAttributeIds: [],
  },
  game: {
    contentType: "game",
    personLabel: "制作者",
    personRoles: ["creator"],
    makerLabel: "メーカー・ブランド",
    attributeIds: ["at-platform"],
    providerAttributeIds: ["at-platform"],
  },
  goods: {
    contentType: "goods",
    personLabel: "ブランド",
    personRoles: ["creator"],
    makerLabel: "ブランド・メーカー",
    attributeIds: ["at-size"],
    providerAttributeIds: [],
  },
  other: {
    contentType: "other",
    personLabel: "作者・出演者",
    personRoles: ["creator", "performer"],
    makerLabel: "メーカー・出版社",
    attributeIds: [],
    providerAttributeIds: [],
  },
};

/** 表示名・条件見出しはユメサーチ側で補完している（外部APIでは提供されない想定） */
export const CATEGORY_LABEL_SOURCE = "local" as const;